/**
 * PricingTable — Reusable pricing grid for both authenticated and unauthenticated users.
 *
 * Props:
 *   currentTier  - string ('free'|'pro'|'entrepreneur') or null for unauthenticated
 *   onSelectTier - (tierKey, interval) => void — called when user clicks a CTA button
 *   isAuthenticated - boolean
 *   checkoutLoading - string|null — loading state key like 'pro_monthly'
 *
 * Founding offer: during the launch window the monthly price for pro/entrepreneur
 * is shown at the founding rate (standard struck through) — but ONLY for a
 * new-checkout buyer (unauthenticated, or currently free/lapsed). An existing
 * paid subscriber switching tiers goes through Stripe Subscription.modify, which
 * gets no coupon, so they keep seeing standard prices. This mirrors exactly when
 * the backend actually applies the discount.
 */

import { Crown, Zap, Rocket, Check, Loader2 } from 'lucide-react';
import { TIERS, TIER_ORDER } from '../lib/tiers';
import useFoundingOffer from '../lib/useFoundingOffer';
import FoundingBanner from './FoundingBanner';

const TIER_ICONS = {
  free: Zap,
  pro: Crown,
  entrepreneur: Rocket,
};

export default function PricingTable({ currentTier, onSelectTier, isAuthenticated, checkoutLoading, trialEligible, highlightTier = null }) {
  const offer = useFoundingOffer();
  // New-checkout buyers get the founding coupon; existing paid subscribers (who
  // switch via Subscription.modify) do not.
  const isNewBuyer = !isAuthenticated || !currentTier || currentTier === 'free';

  return (
    <>
      <FoundingBanner offer={offer} className="mb-4" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {TIER_ORDER.map(tierKey => {
          const tier = TIERS[tierKey];
          const isCurrent = isAuthenticated && tierKey === currentTier;
          const currentIdx = TIER_ORDER.indexOf(currentTier);
          const tierIdx = TIER_ORDER.indexOf(tierKey);
          const isUpgrade = isAuthenticated && tierIdx > currentIdx;
          const isDowngrade = isAuthenticated && tierIdx < currentIdx;
          const Icon = TIER_ICONS[tierKey];

          const foundingPrice = (offer && offer.founding_prices && isNewBuyer)
            ? offer.founding_prices[tierKey]
            : null;
          const monthlyShown = foundingPrice != null ? foundingPrice : tier.monthlyPrice;

          return (
            <div
              key={tierKey}
              className={`rounded-lg p-4 border ${
                isCurrent
                  ? 'bg-slate-700/50 border-blue-600/50'
                  : 'bg-slate-800/40 border-slate-700/50'
              } ${highlightTier === tierKey ? 'ring-2 ring-indigo-500/70' : ''}`}
            >
              <div className="flex items-center gap-2 mb-3">
                <Icon size={16} className={
                  tierKey === 'entrepreneur' ? 'text-amber-400'
                  : tierKey === 'pro' ? 'text-blue-400'
                  : 'text-slate-400'
                } />
                <span className="text-sm font-medium text-slate-200">{tier.name}</span>
                {isCurrent && (
                  <span className="text-[11px] bg-blue-600/30 text-blue-300 px-1.5 py-0.5 rounded">Current</span>
                )}
              </div>

              <div className="mb-3">
                {foundingPrice != null ? (
                  <>
                    <span className="text-xl font-semibold text-amber-300">${foundingPrice}</span>
                    <span className="text-xs text-slate-500">/mo</span>
                    <span className="ml-2 text-sm text-slate-500 line-through">${tier.monthlyPrice}</span>
                    <div className="text-[11px] text-amber-400 mt-0.5">Founding price, locked for life</div>
                  </>
                ) : (
                  <>
                    <span className="text-xl font-semibold text-slate-100">${tier.monthlyPrice}</span>
                    <span className="text-xs text-slate-500">/mo</span>
                    {tier.annualPrice && (
                      <div className="text-xs text-slate-500 mt-0.5">
                        or ${tier.annualPrice}/mo billed annually
                      </div>
                    )}
                  </>
                )}
              </div>

              <ul className="space-y-1.5 mb-4">
                {tier.highlights && tier.highlights.map((f, i) => (
                  <li key={`h-${i}`} className="flex items-start gap-1.5 text-xs text-amber-300 font-medium">
                    <Check size={12} className="text-amber-400 mt-0.5 flex-shrink-0" />
                    {f}
                  </li>
                ))}
                {tier.includesFrom && (
                  <li className="text-xs text-slate-500 pt-1 pb-0.5">
                    Everything in {TIERS[tier.includesFrom]?.name}, plus:
                  </li>
                )}
                {tier.features.map((f, i) => {
                  // Returnees (authenticated, not trial-eligible) don't get a second
                  // trial — don't dangle a "start a trial" bullet at them.
                  const label = (tierKey === 'free' && isAuthenticated && !trialEligible
                    && f === 'Start a 14-day Pro trial anytime')
                    ? 'Resubscribe anytime'
                    : f;
                  return (
                    <li key={i} className="flex items-start gap-1.5 text-xs text-slate-400">
                      <Check size={12} className="text-green-500 mt-0.5 flex-shrink-0" />
                      {label}
                    </li>
                  );
                })}
              </ul>

              {/* Unauthenticated: show Get Started / Sign Up CTAs */}
              {!isAuthenticated && (
                <button
                  onClick={() => onSelectTier(tierKey, 'monthly')}
                  className={`w-full text-xs py-2 rounded-md transition-colors ${
                    tierKey === 'free'
                      ? 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                      : 'bg-blue-600 hover:bg-blue-500 text-white'
                  }`}
                >
                  {tierKey === 'free' ? 'Get Started Free' : `Get Started — $${monthlyShown}/mo`}
                </button>
              )}

              {/* Authenticated: upgrade buttons */}
              {isUpgrade && (
                <div className="space-y-2">
                  <button
                    onClick={() => onSelectTier(tierKey, 'monthly')}
                    disabled={checkoutLoading !== null}
                    className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 text-white text-xs py-2 rounded-md transition-colors flex items-center justify-center gap-1.5"
                  >
                    {checkoutLoading === `${tierKey}_monthly` ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : trialEligible ? (
                      <>Start 14-day free trial &mdash; then ${monthlyShown}/mo</>
                    ) : (
                      <>Upgrade &mdash; ${monthlyShown}/mo</>
                    )}
                  </button>
                  {tier.annualPrice && (
                    <button
                      onClick={() => onSelectTier(tierKey, 'annual')}
                      disabled={checkoutLoading !== null}
                      className="w-full bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 text-slate-300 text-xs py-2 rounded-md transition-colors flex items-center justify-center gap-1.5"
                    >
                      {checkoutLoading === `${tierKey}_annual` ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : trialEligible ? (
                        <>Free trial, then ${tier.annualPrice}/mo billed annually</>
                      ) : (
                        <>Annual &mdash; ${tier.annualPrice}/mo (save ${(tier.monthlyPrice - tier.annualPrice) * 12}/yr)</>
                      )}
                    </button>
                  )}
                </div>
              )}

              {/* Authenticated: downgrade buttons */}
              {isDowngrade && tierKey !== 'free' && (
                <div className="space-y-2">
                  <button
                    onClick={() => onSelectTier(tierKey, 'monthly')}
                    disabled={checkoutLoading !== null}
                    className="w-full bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 text-slate-300 text-xs py-2 rounded-md transition-colors flex items-center justify-center gap-1.5"
                  >
                    {checkoutLoading === `${tierKey}_monthly` ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <>Switch to {tier.name} &mdash; ${tier.monthlyPrice}/mo</>
                    )}
                  </button>
                </div>
              )}

              {/* Authenticated: downgrade to free — point to Manage Subscription */}
              {isDowngrade && tierKey === 'free' && (
                <p className="text-xs text-slate-500 text-center">Use "Manage Subscription" to cancel</p>
              )}

              {/* Current plan label */}
              {isCurrent && currentTier !== 'free' && (
                <p className="text-xs text-slate-500 text-center">Your current plan</p>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
